"use client";

import { Crown, Gift, Star } from "lucide-react";
import { useState } from "react";
import { CreditsBanner } from "@/components/landing/pricing-section/CreditsBanner";
import { PricingTrustRow } from "@/components/landing/pricing-section/PricingTrustRow";
import {
  FREE_TRIAL_PRICE_MAD,
  PAYG_MAX_GENERATIONS,
  PAYG_MIN_GENERATIONS,
  PAYG_PRICE_PER_GEN_MAD,
  PRO_SEMESTER_MAD,
  PRO_YEARLY_MAD,
  STARTER_SEMESTER_MAD,
  STARTER_YEARLY_MAD,
} from "@/lib/pricingConstants";
import { BillingCycleToggle } from "./BillingCycleToggle";
import { PaygCreditsCard } from "./PaygCreditsCard";
import { PricingPageCard } from "./PricingPageCard";
import {
  CREDITS_BANNER_COPY,
  CURRENCY,
  formatMadAmount,
  PRICING_TRUST_ITEMS,
  SUBSCRIPTION_PLAN_COPY,
  type BillingCycle,
} from "./pricingPageCopy";

const PLAN_ICONS = {
  freeTrial: Gift,
  starter: Star,
  pro: Crown,
} as const;

const PLAN_PRICES = {
  freeTrial: {
    kind: "free",
    amount: FREE_TRIAL_PRICE_MAD,
    period: "/ 7 days",
  },
  starter: {
    kind: "subscription",
    semester: STARTER_SEMESTER_MAD,
    yearly: STARTER_YEARLY_MAD,
  },
  pro: {
    kind: "subscription",
    semester: PRO_SEMESTER_MAD,
    yearly: PRO_YEARLY_MAD,
  },
} as const;

export function PricingCardsGrid() {
  const [billingCycle, setBillingCycle] = useState<BillingCycle>("semester");

  return (
    <section
      className="relative isolate overflow-hidden bg-white px-4 py-12 text-slate-950 sm:px-6 sm:py-16 lg:px-8"
      aria-label="Pricing plans"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-slate-200"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute left-1/2 top-20 -z-10 h-[520px] w-[min(92vw,1180px)] -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.11),rgba(234,88,12,0.08)_38%,rgba(8,166,47,0.06)_58%,transparent_74%)] blur-3xl"
        aria-hidden
      />

      <div className="mx-auto max-w-[1440px]">
        <div className="flex justify-center">
          <BillingCycleToggle defaultValue={billingCycle} onChange={setBillingCycle} />
        </div>

        <div className="mx-auto mt-10 grid max-w-[1320px] gap-6 md:grid-cols-2 xl:grid-cols-4 xl:items-stretch">
          {SUBSCRIPTION_PLAN_COPY.map((plan) => (
            <PricingPageCard
              key={plan.id}
              variant={plan.variant}
              icon={PLAN_ICONS[plan.id]}
              label={plan.label}
              tagline={plan.tagline}
              features={plan.features}
              cta={plan.cta}
              price={PLAN_PRICES[plan.id]}
              activeBillingCycle={billingCycle}
              badge={plan.badge}
            />
          ))}
          <PaygCreditsCard />
        </div>

        <div className="mx-auto mt-6 max-w-[1200px] space-y-5">
          <CreditsBanner
            copy={CREDITS_BANNER_COPY}
            minCredits={PAYG_MIN_GENERATIONS}
            maxCredits={PAYG_MAX_GENERATIONS}
            pricePerCredit={`${formatMadAmount(PAYG_PRICE_PER_GEN_MAD)} ${CURRENCY}`}
          />
          <PricingTrustRow items={PRICING_TRUST_ITEMS} />
        </div>
      </div>
    </section>
  );
}
